import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import {
  validateScheduledFirstCommunicationDate,
  parseDateOfService,
  validateInputDatePicker,
  validateInputTime,
} from "pages/CreateOrder/utils/helpers";
import {
  handleChangeDateWithMask,
  handleChangeTimeWithMask,
} from "pages/CreateOrder/utils/mask";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useContext, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useUpdateOrderMutation } from "store/queries/order";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";

const NOT_SCHEDULED_DATE = "01/01/2999";

const orderTypeToLocationKey: Partial<Record<string, string>> = {
  [orderTypeConstants.BUNDLED]: "bundled",
  [orderTypeConstants.PATIENT_RESPONSIBILITY]: "patientResponsibility",
  [orderTypeConstants.ED]: "ed",
  [orderTypeConstants.BARIATRICS]: "bariatrics",
  [orderTypeConstants.GFE]: "gfe",
};
function isLocationEnabledForOrderType(
  orderType: string,
  locationEnabled?: Record<string, boolean>
): boolean {
  const key = orderTypeToLocationKey[orderType];
  return !!(key && locationEnabled?.[key]);
}

interface IVisitEdit {
  dateOfService: string;
  accountNumber: string;
  timeOfService: string;
  scheduledFirstCommunicationDate?: Date | null | string;
  scheduledFirstCommunicationSent: boolean;
  scheduledCommunicationCustomText: string | null;
  accountLocationId?: string | null;
  directPayProviderId?: string | null;
  insuranceName?: string | null;
  notScheduledYet?: boolean;
}

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  border: 1px solid var(--gray-new-200) !important;
`;

function VisitDetails({
  order,
  onUpdateOrder,
}: {
  order: IOrder;
  onUpdateOrder: () => void;
}) {
  const {
    control,
    trigger,
    getValues,
    setValue,
    setError,
    formState: { errors, dirtyFields, isValid },
    watch,
  } = useFormContext<IVisitEdit>();
  const { current: toastElement } = useContext(ToastContext);
  const [updateOrder] = useUpdateOrderMutation();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const [editing, setEdit] = useState(false);
  const [editingAmount, setEditAmount] = useState(false);
  const scheduledFirstCommunicationDate = watch(
    "scheduledFirstCommunicationDate"
  );
  const scheduledFirstCommunicationSent = watch(
    "scheduledFirstCommunicationSent"
  );
  const dateOfService = watch("dateOfService");
  const timeOfService = watch("timeOfService");
  const accountNumber = watch("accountNumber");
  const notScheduledYet = watch("notScheduledYet");
  useEffect(() => {
    const initialDateOfService = order.dateOfService;
    const isNotScheduled =
      parseDate(initialDateOfService) === NOT_SCHEDULED_DATE;
    setValue("notScheduledYet", isNotScheduled);
    setValue(
      "dateOfService",
      isNotScheduled ? NOT_SCHEDULED_DATE : parseDate(order.dateOfService)
    );
    setValue("accountNumber", order.accountNumber);
    setValue(
      "timeOfService",
      isNotScheduled ? "" : parseTime(order.dateOfService)
    );
    setValue(
      "scheduledFirstCommunicationDate",
      order?.scheduledFirstCommunicationDate
        ? dayjs.utc(order.scheduledFirstCommunicationDate).toDate()
        : null
    );
    setValue(
      "scheduledFirstCommunicationSent",
      order?.scheduledFirstCommunicationSent || false
    );
    setValue(
      "accountLocationId",
      order?.accountLocation?.id ?? order?.accountLocationId ?? null
    );
    setValue(
      "directPayProviderId",
      order?.directPayProvider?.id ?? order?.directPayProviderId ?? null
    );
    setValue("insuranceName", order?.insuranceName ?? null);
  }, [order, setValue]);

  const showScheduledDate = useMemo(() => {
    if (scheduledFirstCommunicationDate && scheduledFirstCommunicationSent) {
      return false;
    }
    return true;
  }, [scheduledFirstCommunicationDate, scheduledFirstCommunicationSent]);

  const showLocationField = useMemo(() => {
    if (order.accountLocation) {
      return true;
    }
    const { account } = order;
    return (
      isLocationEnabledForOrderType(
        order.orderType,
        account?.locationEnabled
      ) && (account?.accountLocations?.length ?? 0) > 0
    );
  }, [
    order.accountLocation,
    order.orderType,
    order.account?.locationEnabled,
    order.account?.accountLocations,
  ]);

  // Options are the link's allowed providers - the same set the patient chose from - so an
  // edit here can never produce a value the create path would have rejected.
  const directPayProviderOptions = useMemo(() => {
    const fromLink =
      order.directPayLink?.providers?.map((p) => p.provider) ?? [];
    if (fromLink.length) {
      return fromLink;
    }
    return order.directPayProvider ? [order.directPayProvider] : [];
  }, [order.directPayLink?.providers, order.directPayProvider]);

  const showDirectPayProviderField = useMemo(() => {
    if (order.directPayProvider) {
      return true;
    }
    return (
      !!order.directPayLink?.showProviderName &&
      directPayProviderOptions.length > 0
    );
  }, [
    order.directPayProvider,
    order.directPayLink?.showProviderName,
    directPayProviderOptions,
  ]);

  // Insurance name is only ever captured on the Direct Pay form, so it stays off the Visit card
  // for regular orders. An order that already has one always shows it, even if the link's toggle
  // was turned off afterwards.
  const showInsuranceNameField = useMemo(() => {
    return !!order.insuranceName || !!order.directPayLink?.showInsuranceName;
  }, [order.insuranceName, order.directPayLink?.showInsuranceName]);

  const saveButtonEnabled = useMemo(() => {
    const initialDateOfService = order.dateOfService;
    const isInitialDateInPast = dayjs(initialDateOfService).isBefore(
      dayjs(),
      "day"
    );

    const dateOfServiceIsValid =
      isInitialDateInPast || notScheduledYet
        ? !!dateOfService // If initial date is in past, just check if date exists
        : dateOfService &&
          validateInputDatePicker(dateOfService, "future") === true;

    const timeOfServiceIsValid = timeOfService
      ? validateInputTime(timeOfService) === true
      : true;
    const accountNumberIsValid = !!accountNumber;
    const scheduledFirstCommunicationDateIsValid =
      validateScheduledFirstCommunicationDate(
        scheduledFirstCommunicationDate,
        dateOfService,
        scheduledFirstCommunicationSent
      ) === true;

    return (
      dateOfServiceIsValid &&
      timeOfServiceIsValid &&
      accountNumberIsValid &&
      scheduledFirstCommunicationDateIsValid
    );
  }, [
    order.dateOfService,
    dateOfService,
    timeOfService,
    accountNumber,
    scheduledFirstCommunicationDate,
    scheduledFirstCommunicationSent,
  ]);

  useEffect(() => {
    validateScheduledFirstCommunicationDate(
      scheduledFirstCommunicationDate,
      dateOfService,
      scheduledFirstCommunicationSent
    );
    trigger("scheduledFirstCommunicationDate");
  }, [
    scheduledFirstCommunicationDate,
    dateOfService,
    scheduledFirstCommunicationSent,
  ]);

  const resetForm = () => {
    const isNotScheduled =
      parseDate(order.dateOfService) === NOT_SCHEDULED_DATE;
    setValue("notScheduledYet", isNotScheduled);
    setValue(
      "dateOfService",
      isNotScheduled ? NOT_SCHEDULED_DATE : parseDate(order.dateOfService)
    );
    setValue("accountNumber", order.accountNumber);
    setValue(
      "timeOfService",
      isNotScheduled ? "" : parseTime(order.dateOfService)
    );
    setValue(
      "scheduledFirstCommunicationDate",
      order?.scheduledFirstCommunicationDate
        ? dayjs.utc(order.scheduledFirstCommunicationDate).toDate()
        : null
    );
    setValue(
      "scheduledFirstCommunicationSent",
      order?.scheduledFirstCommunicationSent || false
    );
    setValue(
      "accountLocationId",
      order?.accountLocation?.id ?? order?.accountLocationId ?? null
    );
    setValue(
      "directPayProviderId",
      order?.directPayProvider?.id ?? order?.directPayProviderId ?? null
    );
    setValue("insuranceName", order?.insuranceName ?? null);
  };

  const mapServiceDetailsUpdateRequest = (values: IVisitEdit) => ({
    dateOfService: parseDateOfService({
      dateOfService: values.dateOfService,
      timeOfService: values.timeOfService,
    }),
    accountNumber: values.accountNumber,
    scheduledFirstCommunicationDate: values.scheduledFirstCommunicationDate
      ? dayjs(values.scheduledFirstCommunicationDate)
          .utc()
          .format("YYYY-MM-DD HH:mm")
      : null,
    accountLocationId: values.accountLocationId || null,
    ...(showDirectPayProviderField
      ? { directPayProviderId: values.directPayProviderId || null }
      : {}),
    ...(showInsuranceNameField
      ? { insuranceName: values.insuranceName?.trim() || null }
      : {}),
  });

  const submit = async () => {
    const isValid = await trigger("scheduledFirstCommunicationDate");
    if (!isValid) {
      return;
    }
    const formValues = getValues();
    const initialDateOfService = parseDate(order.dateOfService);
    const initialTimeOfService = parseTime(order.dateOfService);
    const dateOrTimeOfServiceChanged =
      formValues.dateOfService !== initialDateOfService ||
      formValues.timeOfService !== initialTimeOfService;

    const values = mapServiceDetailsUpdateRequest(formValues);
    updateOrder({
      id: order.id,
      ...values,
      ...(dateOrTimeOfServiceChanged ? { orderAttemptNumber: 1 } : {}),
    })
      .unwrap()
      .then(() => {
        onUpdateOrder();
        toastElement?.show({
          summary: "Success!",
          severity: "success",
          detail: "Your estimate has been updated.",
        });
        setEdit(false);
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  const actionFooter = () => {
    return (
      <>
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={submit}
          disabled={!saveButtonEnabled}
        />
        <Button
          label="Cancel"
          onClick={() => {
            resetForm();
            setEdit(false);
            setEditAmount(false);
          }}
          icon="pi pi-times"
          style={{ marginLeft: "0.5em" }}
          className="p-button-outlined-light"
        />
      </>
    );
  };

  const actionHeader = () => {
    return (
      <div className="flex flex-column col-12 align-items-end">
        <div>
          <Button
            icon="pi pi-pencil"
            onClick={() => setEdit(true)}
            className="p-button-outlined-light"
          />
        </div>
      </div>
    );
  };
  return (
    <StyledCard title="Visit" header={!editing && actionHeader()}>
      {!editing && notScheduledYet && (
        <div className="flex align-items-center mb-3 gap-2">
          <i
            className="pi pi-calendar-times"
            style={{ color: "var(--orange-500)" }}
          />
          <p
            className="mt-0 "
            style={{ color: "var(--orange-500)", fontWeight: 600 }}
          >
            Not scheduled
          </p>
        </div>
      )}
      {editing && (
        <div className="flex align-items-center mb-3">
          <Controller
            name="notScheduledYet"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                inputId="notScheduledYet"
                data-testid="notScheduledYet"
                onChange={(e) => {
                  const checked = e.checked ?? false;
                  onChange(checked);
                  if (checked) {
                    setValue("dateOfService", NOT_SCHEDULED_DATE);
                    setValue("timeOfService", "");
                  } else {
                    setValue("dateOfService", "");
                    setValue("timeOfService", "");
                  }
                }}
                checked={!!value}
              />
            )}
          />
          <label htmlFor="notScheduledYet" className="ml-2">
            Not scheduled
          </label>
        </div>
      )}
      <div className="formgrid grid w-100">
        {!notScheduledYet && (
          <>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="dateOfService">Date of service</Label>

              <Controller
                data-testid="dateOfService"
                name="dateOfService"
                control={control}
                rules={{
                  validate: (date) => {
                    const initialDateOfService = order.dateOfService;
                    const isInitialDateInPast = dayjs(
                      initialDateOfService
                    ).isBefore(dayjs(), "day");

                    if (isInitialDateInPast || notScheduledYet) {
                      // If initial date is in past, just validate it's a valid date
                      return validateInputDatePicker(date, "any");
                    }
                    // Otherwise, validate it's a future date
                    return validateInputDatePicker(date, "future");
                  },
                  required: validationConstants.VISIT_INFO_FORM.DATE_OF_SERVICE,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      disabled={!editing}
                      id="dateOfService"
                      value={value || ""}
                      placeholder="99/99/9999"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "dateOfService",
                          trigger,
                        })
                      }
                      onChange={(e) => handleChangeDateWithMask(e, onChange)}
                    />

                    {getFormErrorMessage("dateOfService", errors)}
                  </>
                )}
              />
            </div>

            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="timeOfService">Time of service</Label>

              <Controller
                control={control}
                name="timeOfService"
                data-testid="timeOfService"
                rules={{
                  validate: (time) => validateInputTime(time),
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <InputText
                      disabled={!editing}
                      id="timeOfService"
                      value={value}
                      placeholder="HH:MM PM"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "timeOfService",
                          trigger,
                        })
                      }
                      onChange={(e) => handleChangeTimeWithMask(e, onChange)}
                    />

                    {getFormErrorMessage("timeOfService", errors)}
                  </>
                )}
              />
            </div>
          </>
        )}

        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="accountId">Account/ Visit ID</Label>

          <Controller
            name="accountNumber"
            control={control}
            rules={{
              required: validationConstants.VISIT_INFO_FORM.VISIT_ID,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <InputText
                  disabled={!editing}
                  data-testid="accountNumber"
                  id="accountNumber"
                  placeholder="Account ID"
                  autoComplete="off"
                  value={value}
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "accountNumber",
                      trigger,
                    })
                  }
                  onChange={(e) => onChange(e.target.value)}
                />

                {getFormErrorMessage("accountNumber", errors)}
              </>
            )}
          />
        </div>
        {showLocationField && (
          <div className="field flex flex-column col-12 md:col-4 lg:col-3">
            <Label htmlFor="accountLocationId">Location</Label>
            <Controller
              data-testid="accountLocationId"
              name="accountLocationId"
              control={control}
              render={({ field: { onChange, value } }) =>
                editing ? (
                  <Dropdown
                    key={value ?? "empty"}
                    id="accountLocationId"
                    data-testid="accountLocationId"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.value ?? null)}
                    options={
                      order.account?.accountLocations ?? [order.accountLocation]
                    }
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select location"
                    className="w-full"
                    showClear
                    filter
                    filterBy="name"
                    filterPlaceholder="Search by name"
                  />
                ) : (
                  <InputText
                    disabled
                    id="accountLocationId"
                    value={order.accountLocation?.name ?? "—"}
                  />
                )
              }
            />
          </div>
        )}
        {showDirectPayProviderField && (
          <div className="field flex flex-column col-12 md:col-4 lg:col-3">
            <Label htmlFor="directPayProviderId">Provider</Label>
            <Controller
              data-testid="directPayProviderId"
              name="directPayProviderId"
              control={control}
              render={({ field: { onChange, value } }) =>
                editing ? (
                  <Dropdown
                    key={value ?? "empty"}
                    id="directPayProviderId"
                    data-testid="directPayProviderId"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.value ?? null)}
                    options={directPayProviderOptions}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select provider"
                    className="w-full"
                    showClear
                    filter
                    filterBy="name"
                    filterPlaceholder="Search by name"
                  />
                ) : (
                  <InputText
                    disabled
                    id="directPayProviderId"
                    value={order.directPayProvider?.name ?? "—"}
                  />
                )
              }
            />
          </div>
        )}
        {showInsuranceNameField && (
          <div className="field flex flex-column col-12 md:col-4 lg:col-3">
            <Label htmlFor="insuranceName">Insurance Name</Label>
            <Controller
              data-testid="insuranceName"
              name="insuranceName"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputText
                  disabled={!editing}
                  id="insuranceName"
                  data-testid="insuranceName"
                  autoComplete="off"
                  placeholder={editing ? "Insurance Name" : "—"}
                  value={value || ""}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
            />
          </div>
        )}
        {showScheduledDate && (
          <div className="field flex flex-column col-12 md:col-8 lg:col-3">
            <Label htmlFor="dateOfService">
              Scheduled first communication date
            </Label>

            <Controller
              data-testid="scheduledFirstCommunicationDate"
              name="scheduledFirstCommunicationDate"
              control={control}
              rules={{
                validate: (date) =>
                  validateScheduledFirstCommunicationDate(
                    date,
                    dateOfService,
                    scheduledFirstCommunicationSent
                  ),
              }}
              render={({ field: { value } }) => (
                <>
                  <Calendar
                    showIcon
                    placeholder="Schedule first message"
                    value={value}
                    todayButtonClassName="hidden"
                    disabled={
                      !!scheduledFirstCommunicationSent ||
                      !scheduledFirstCommunicationDate ||
                      !editing
                    }
                    minDate={dayjs().add(2, "minute").toDate()}
                    maxDate={dayjs(dateOfService, "MM/DD/YYYY")
                      .endOf("day")
                      .toDate()}
                    onChange={(e) => {
                      // Prevent clearing the date - only allow changing to a new date
                      if (e.value) {
                        setValue(
                          "scheduledFirstCommunicationDate",
                          e.value as string,
                          {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true,
                          }
                        );
                      }
                    }}
                    showTime
                    hourFormat="12"
                    panelStyle={{
                      display: "flex",
                      gap: "2rem",
                    }}
                  />

                  {getFormErrorMessage(
                    "scheduledFirstCommunicationDate",
                    errors
                  )}
                </>
              )}
            />
          </div>
        )}
      </div>
      {editing && actionFooter()}
    </StyledCard>
  );
}

export default VisitDetails;
